import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { FaEye, FaEyeSlash, FaExclamationTriangle } from "react-icons/fa";
import styled from "styled-components";
import openNotification from "../utils/CustomNotification";

interface LocationState {
  userData?: string;
  sessionUid: string;
  userUid: string;
  userorgData?: any;
  usersiteData?: any;
  formData?: any;
  two_fa_enabled?: boolean;
  userDetails: {
    orgUid: string;
    siteUid: string;
    privilege: string;
    user_role: string;
    device_control: boolean;
  };
}

interface ApiResponse<T = any> {
  status: string;
  data: T;
  message?: string;
}

const ForcedResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email1 = localStorage.getItem("email1") || "";
  const twoFaEnabled = localStorage.getItem("two_fa_enabled");
  const {
    user_uid,
    sessionUid,
    userUid,
    org_uid,
    site_uid,
    privilege,
    user_role,
    device_control,
  } = location.state || {};


  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const prevPassword = useRef<string>("");


  const [isPasswordReset, setIsPasswordReset] = useState<boolean>(false);
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleGeneratePassword = async () => {
    const params = {
      target_user_uid: user_uid,
    };

    try {
      const apiResponse = await axios.get<ApiResponse<string>>(
        `${beAddress}/api/generate-user-password-forced`,
        {
          headers: { "user-uid": userUid }, withCredentials: true,
          params,
        }
      );

      if (apiResponse.status === 200 && apiResponse.data.status === "Success") {
        setNewPassword(apiResponse.data.data);
        setIsPasswordReset(true);
        setShowPasswordField(true);
      } else {
        openNotification({
          type: "error",
          message: apiResponse.data.data || apiResponse.data.message || "Unknown error",
        });
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "Failed to generate password",
      });
    }
  };

  const handleForgotPasswords = async () => {
    try {
      const headers = {
        accept: "application/json",
        "session-uid": sessionUid,
        "user-uid": userUid,
      };

      const targeted_user_records = {
        user_uid: user_uid,
        org_uid: org_uid,
        site_uid: site_uid,
        privilege: privilege,
        password: newPassword,
        user_role: user_role,
        device_control: device_control,
        two_fa_enabled: twoFaEnabled,
        email: email1,

      };


      const response = await axios.put<ApiResponse>(
        `${beAddress}/api/update-user-password-forced`,
        targeted_user_records,
        { headers,withCredentials: true,}
      );

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.data || response.data.message,
        });
        navigate(-1);
      } else {
        openNotification({
          type: "error",
          message: response.data.data || response.data.message || "Error occurred",
        });
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      openNotification({
        type: "error",
        message: "Failed to get response from server",
      });
    }
  };

  const handleCancel = () => {
    setIsPasswordReset(false);
    setShowPasswordField(false);
    setNewPassword("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <StyledWrapper>
      <Container>
        <Title>Forced Reset Password</Title>

        <Label>User Email</Label>
        <Input type="email" value={user_uid} readOnly />

        {!showPasswordField && (
          <GenerateBtn onClick={handleGeneratePassword}>
            Generate Password
          </GenerateBtn>
        )}

        {showPasswordField && (
          <>
            <Label>Generated Password</Label>
            <RelativeWrapper>
              <PasswordInput
                type={showPassword ? "text" : "password"}
                value={newPassword}
                disabled
              />
              <EyeIconBtn onClick={togglePasswordVisibility}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </EyeIconBtn>
            </RelativeWrapper>

            {/* <TwofaNoteBlock>
              <NoteIcon />
              <span>
                Note: Two factor authentication is disabled for the user
              </span>
            </TwofaNoteBlock> */}

            <ButtonGroup>
              <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
              <SubmitBtn onClick={handleForgotPasswords}>Submit</SubmitBtn>
            </ButtonGroup>
          </>
        )}
      </Container>
    </StyledWrapper>
  );
};

export default ForcedResetPassword;

// --- Styled Components ---
const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f4f4f4;
`;

const Container = styled.div`
  padding: 1.5rem;
  max-width: 30rem;
  width: 100%;
  border: 0.1rem solid #ccc;
  border-radius: 0.75rem;
  background-color: #fff;
  box-shadow: 0 0 0.625rem rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.375rem;
  color: #444;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 0.1rem solid #ccc;
  border-radius: 0.5rem;
  width: 100%;
  background-color: #f3f3f3;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 1rem;
  box-sizing: border-box;
`;

const PasswordInput = styled(Input)`
  font-weight: 600;
  padding-right: 3rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
`;

const GenerateBtn = styled(Button)`
  background-color: #225963;
  color: white
`;

const CancelBtn = styled(Button)`
  background-color: #225963;
  border: 1px solid black;
  color: white;
  width: 48%;
`;

const SubmitBtn = styled(Button)`
  background-color: #225963;
  color: white;
  width: 48%;
`;

const EyeIconBtn = styled.button`
  position: absolute;
  top: 40%;
  right: 0.75rem;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 1.25rem;
  height: 1.5rem;
  width: 1.5rem;
  cursor: pointer;
`;

const RelativeWrapper = styled.div`
  position: relative;
`;

const TwofaNoteBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 1rem;
  font-size: 0.9rem;
  color: #555;
  flex-wrap: wrap;
  border: 0.1rem solid #ccc;
  border-radius: 0.5rem;
  background-color: #fef8e7;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const NoteIcon = styled(FaExclamationTriangle)`
  color: #e6a000;
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
`;
